import _ from 'underscore';
import { assertIsDefined } from '@bingads-webui-campaign-react/debug-assert';
import { WithTreeViewData } from '@bingads-webui-grid/well-known';
import { IDropdownOption } from '@fluentui/react';
import { Model$ListingGroup } from '../ApiType';
import { EVERYTHING_ELSE_NODE_ATTRIBUTE, NullPartial } from '../constant';
import { ListingGroupGridItemData } from '../interface';
import { ListingGroupTreeViewSelectorGridItem } from './panel-helper';

function getKeyOfHotelListingGroupItem(item: ListingGroupGridItemData) { return `${item.Condition?.Operand ?? ''}-${item.Condition?.Attribute ?? ''}`; }
function getAttributeValueOfPanelSelectorItem(item: ListingGroupTreeViewSelectorGridItem) { return item.data.Attribute; }
function getKeyOfPanelSelectorItem(item: ListingGroupTreeViewSelectorGridItem, diemnsionKey: string) { return `${diemnsionKey}-${getAttributeValueOfPanelSelectorItem(item) ?? ''}`; }

export interface TreeViewSaveWorkerOptions {
  selectedItems: ListingGroupTreeViewSelectorGridItem[];
  selectedDimension: IDropdownOption<any>;
  treeItem: WithTreeViewData<ListingGroupGridItemData>;

  // / Need adGroup and campaign info to send request

  assetGroupId: number;
  campaignId: number;

  callbackWhenSkipUpdate: () => void;
  callbackWhenUpdate: (
    listingGroupsToAdd: Partial<NullPartial<Model$ListingGroup>>[],
    listingGroupsToDelete: number[],
  ) => Promise<void>;
}

export function getAddAndDeleteItem(selectedDimension: IDropdownOption<any>, selectedItems: ListingGroupTreeViewSelectorGridItem[], treeItem: WithTreeViewData<ListingGroupGridItemData>) {
  // / handle children
  // 1. use `${DimensionType}-${DimensionValue}`(.i.e `${Operand}-${Attribute}`) as key
  // 2. calculate the difference
  // 3. use API to update
  assertIsDefined(treeItem);
  const childrenOfEidtItem = treeItem.treeViewInfo.children;

  const currentOperandOfChildren: string = selectedDimension.data.operand;

  const dummyEveryThingElse: ListingGroupTreeViewSelectorGridItem = {
    key: '_DUMMY_EVERY_THING_ELSE',
    data: { Attribute: EVERYTHING_ELSE_NODE_ATTRIBUTE },
  } as unknown as ListingGroupTreeViewSelectorGridItem;

  const finalSelectedItems = selectedItems.length === 0 ? selectedItems : [
    ...selectedItems,
    dummyEveryThingElse,
  ];

  const oldItemKeys = new Set(childrenOfEidtItem.map(child =>
    getKeyOfHotelListingGroupItem(child)));

  const newItemKeys = new Set(finalSelectedItems.map(item =>
    getKeyOfPanelSelectorItem(item, currentOperandOfChildren)));

  // deleted item ids are the difference of oldItemIds and newItemIds
  const deleteItemIds = childrenOfEidtItem.filter((child) => {
    const key = getKeyOfHotelListingGroupItem(child);
    return !newItemKeys.has(key);
  }).map(child => child.Id);

  // added item ids are the difference of newItemIds and oldItemIds
  const addedItems = finalSelectedItems.filter((item) => {
    const key = getKeyOfPanelSelectorItem(item, currentOperandOfChildren);
    return !oldItemKeys.has(key);
  });

  return { deleteItemIds, addedItems };
}

export async function treeViewSaveWorker(params: TreeViewSaveWorkerOptions) {
  const {
    selectedItems,
    selectedDimension,
    treeItem,
    callbackWhenSkipUpdate,
    callbackWhenUpdate,
    assetGroupId,
    campaignId,
  } = params;

  // / handle children
  // 1. use `${DimensionType}-${DimensionValue}`(.i.e `${Operand}-${Attribute}`) as key
  // 2. calculate the difference
  // 3. use API to update
  assertIsDefined(treeItem);
  const childrenOfEidtItem = treeItem.treeViewInfo.children;

  const prevOperandOfChildren = childrenOfEidtItem.length === 0 ? undefined : childrenOfEidtItem[0].Condition?.Operand;

  const currentOperandOfChildren: string = selectedDimension.data.operand;

  const { addedItems, deleteItemIds } = getAddAndDeleteItem(selectedDimension, selectedItems, treeItem);

  // for all new node, it should have a unique negative ID.
  // There should only be one new root, whose ID is `-1`
  const rootNodeId = -1;
  let negativeId = -2;
  const editItemId = treeItem.Id;
  const wasEditingNodeLeaf = treeItem.treeViewInfo.children.length === 0;
  const isEditingNodeLeaf = selectedItems.length === 0;

  const inheritNode = (() => {
    switch (true) {
      case wasEditingNodeLeaf && !isEditingNodeLeaf:
        // add patition under leaf node, new partition inherits bid from leaf node.
        return treeItem;
      case !wasEditingNodeLeaf && prevOperandOfChildren === currentOperandOfChildren:
        // add partition under non-leaf node,
        // new partition inherits bid from everythingElse at the same level.
        return treeItem.treeViewInfo.children.find(child => child.Condition?.Attribute === EVERYTHING_ELSE_NODE_ATTRIBUTE);
      default:
        return undefined;
    }
  })();

  // children could be add to editing node, which could be
  //   - parent, children should set parent ID to editing node
  //   - leaf, editing node will have a dummy ID and all children should set it as parent
  const parentIdOfChildren = !wasEditingNodeLeaf ? treeItem.Id : rootNodeId;
  assertIsDefined(parentIdOfChildren);

  const listingGroupsToAdd: Partial<NullPartial<Model$ListingGroup>>[] = addedItems.map(item => buildListingGroupToAdd({
    Attribute: getAttributeValueOfPanelSelectorItem(item),
    Operand: currentOperandOfChildren,
    ParentId: parentIdOfChildren,
    isLeaf: true,
    inheritNode,
    isDeleteAll: false,
  }));

  const listingGroupsToDelete = Array.from(deleteItemIds);

  // if nothing is changed, just skip
  if (listingGroupsToAdd.length === 0 && listingGroupsToDelete.length === 0) {
    callbackWhenSkipUpdate();
    return;
  }

  // handle current node itself and "everything else" node.
  switch (true) {
    case wasEditingNodeLeaf && !isEditingNodeLeaf:
      // if the item is changed from leaf to parent
      //   - delete it and add it back to make back-end happy(make this item a`Subdivision` rather than`Unit`)

      // item itself
      listingGroupsToDelete.push(editItemId);
      listingGroupsToAdd.push(buildListingGroupToAdd({
        ParentId: treeItem.ParentId ?? undefined,
        Attribute: treeItem.Condition?.Attribute ?? null,
        Operand: (!treeItem.Condition || treeItem.Condition.Operand === '') ? 'All' : treeItem.Condition.Operand,
        isLeaf: false,
        inheritNode,
        isDeleteAll: _.isEmpty(listingGroupsToAdd),
      }));
      break;
    case isEditingNodeLeaf:
      // Note: from leaf to leaf is skipped, for nothing will be changed
      // if the item is changed from parent to leaf, delete it and add it back to make back-end happy(make this item a `Unit` rather than `Subdivision`)
      listingGroupsToDelete.push(editItemId);
      listingGroupsToAdd.push(buildListingGroupToAdd({
        ParentId: treeItem.ParentId ?? undefined,
        Attribute: treeItem.Condition?.Attribute ?? null,
        Operand: (!treeItem.Condition || treeItem.Condition.Operand === '') ? 'All' : treeItem.Condition.Operand,
        isLeaf: true,
        inheritNode,
        isDeleteAll: _.isEmpty(listingGroupsToAdd),
      }));
      break;
    default:
      break;
  }

  // sort the requests to make the root is the first child
  listingGroupsToAdd.sort((a, b) => {
    assertIsDefined(a.Id);
    assertIsDefined(b.Id);
    if (a.Id < b.Id) {
      return 1;
    }
    if (a.Id > b.Id) {
      return -1;
    }
    return 0;
  });

  await callbackWhenUpdate(listingGroupsToAdd, listingGroupsToDelete);

  interface MetaInfo {
    ParentId: number | undefined;
    Attribute: string | null;
    Operand: string | undefined;
    isLeaf: boolean;
    inheritNode: WithTreeViewData<ListingGroupGridItemData> | undefined;
    isDeleteAll: boolean;
  }

  function buildListingGroupToAdd(metaInfo: MetaInfo): Partial<NullPartial<Model$ListingGroup>> {
    return {
      Id: metaInfo.isLeaf ? negativeId-- : rootNodeId,
      ParentId: metaInfo.ParentId,
      IsLeaf: metaInfo.isLeaf,
      AssetGroupId: assetGroupId, // in tree editor page, we always have AdGroupId
      CampaignId: campaignId, // in tree editor page, we always have CampaignId
      // IsAutoBidding: false, // TODO: Not enabled for phase 1
      Condition: { Attribute: metaInfo.Attribute ?? (null as unknown as undefined), Operand: metaInfo.Operand ?? (null as unknown as undefined) },
      IsExcluded: !metaInfo.isDeleteAll && metaInfo.isLeaf && ((metaInfo.Attribute === '' && metaInfo.Operand !== 'All') || (metaInfo.inheritNode?.IsExcluded)),
    };
  }
}
